import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import EmptyState from "@/components/feature/EmptyState";
import SectionHeading from "@/components/feature/SectionHeading";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useFavorites } from "@/hooks/useFavorites";
import { movies } from "@/mocks/movies";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const movie = useMemo(() => movies.find((m) => m.id === id), [id]);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  const favorited = movie ? isFavorite(movie.id) : false;

  usePageMeta({
    title: movie ? `${movie.title} - 生活中的玛丽` : "影视作品 - 生活中的玛丽",
    description: movie
      ? `${movie.title}（${movie.year}·${movie.region}）剧情简介与影评：${movie.synopsis.slice(0, 60)}…`
      : "影视作品详情页",
    keywords: movie
      ? `生活中的玛丽,${movie.title},${movie.title}剧情,${movie.title}影评,${movie.genres.join(",")}`
      : "影视作品,影视详情",
  });

  const related = useMemo(() => {
    if (!movie) return [];
    return movies
      .filter((m) => m.id !== movie.id && m.type === movie.type && m.genres.some((g) => movie.genres.includes(g)))
      .sort((a, b) => b.rating - a.rating)
      .slice(0, 4);
  }, [movie]);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 2200);
  };

  const handleShare = async () => {
    if (!movie) return;
    try {
      await navigator.clipboard.writeText(window.location.href);
      showToast("链接已复制，可分享给好友");
    } catch {
      showToast("复制失败，请手动复制地址栏链接");
    }
  };

  if (!movie) {
    return (
      <div className="w-full px-4 md:px-8 py-14">
        <div className="bg-background-50 rounded-lg border border-background-200">
          <EmptyState
            icon="ri-movie-line"
            title="该影视作品不存在或已下架"
            description="可能是链接已失效，或作品已被移除。你可以返回首页继续浏览其他内容。"
          />
        </div>
        <div className="flex justify-center">
          <Link
            to="/"
            className="mt-6 h-10 px-5 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-medium whitespace-nowrap flex items-center gap-2 transition-colors cursor-pointer"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const typeLabel = movie.type === "movie" ? "电影" : "电视剧";

  return (
    <div className="w-full">
      <div className="bg-primary-500">
        <div className="w-full px-4 md:px-8 pt-6 pb-10 md:pt-8 md:pb-14">
          <div className="text-background-300">
            <Breadcrumb
              items={[
                { label: `${typeLabel}分类`, to: `/category/${movie.type}` },
                { label: movie.title },
              ]}
            />
          </div>

          <div className="mt-6 flex flex-col md:flex-row gap-6 md:gap-10">
            <div className="w-[180px] md:w-[240px] shrink-0 mx-auto md:mx-0">
              <div className="relative w-full aspect-[2/3] rounded-lg overflow-hidden bg-primary-800">
                <img
                  src={movie.cover}
                  alt={`${movie.title} 主封面`}
                  title={`${movie.title} - 影视介绍`}
                  className="w-full h-full object-cover object-top"
                />
                <span className="absolute bottom-2.5 right-2.5 px-2.5 py-1 rounded-md bg-accent-500 text-background-50 text-sm font-bold">
                  {movie.rating.toFixed(1)}
                </span>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="font-heading font-black text-2xl md:text-4xl text-background-50">
                {movie.title}
              </h1>
              <p className="mt-3 text-sm md:text-base text-accent-400 italic">“{movie.tagline}”</p>

              <div className="mt-5 flex flex-wrap gap-2">
                {movie.genres.map((g) => (
                  <span
                    key={g}
                    className="px-2.5 py-1 rounded-md bg-background-50/12 border border-background-50/25 text-background-100 text-xs md:text-sm"
                  >
                    {g}
                  </span>
                ))}
              </div>

              <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                <div className="flex gap-2">
                  <dt className="text-background-300 shrink-0">导演</dt>
                  <dd className="text-background-50">{movie.director}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-background-300 shrink-0">地区</dt>
                  <dd className="text-background-50">{movie.region}</dd>
                </div>
                <div className="flex gap-2 sm:col-span-2">
                  <dt className="text-background-300 shrink-0">主演</dt>
                  <dd className="text-background-50">{movie.cast.join(" / ")}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-background-300 shrink-0">年份</dt>
                  <dd className="text-background-50">{movie.year}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-background-300 shrink-0">时长/集数</dt>
                  <dd className="text-background-50">{movie.length}</dd>
                </div>
                <div className="flex gap-2 sm:col-span-2">
                  <dt className="text-background-300 shrink-0">观看状态</dt>
                  <dd className="text-accent-400">{movie.status}</dd>
                </div>
              </dl>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => toggleFavorite(movie.id)}
                  className={`h-11 px-5 rounded-md text-sm font-medium whitespace-nowrap flex items-center gap-2 transition-colors cursor-pointer border ${
                    favorited
                      ? "bg-accent-500 border-accent-500 text-background-50"
                      : "bg-background-50/12 border-background-50/30 text-background-50 hover:bg-background-50/25"
                  }`}
                >
                  <i className={favorited ? "ri-bookmark-fill text-lg" : "ri-bookmark-line text-lg"}></i>
                  {favorited ? "已收藏" : "收藏作品"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="h-11 px-5 rounded-md bg-background-50/12 border border-background-50/30 text-background-50 hover:bg-background-50/25 text-sm font-medium whitespace-nowrap flex items-center gap-2 transition-colors cursor-pointer"
                >
                  <i className="ri-share-forward-line text-lg"></i>
                  分享
                </button>
              </div>
              <p className="mt-3 text-xs text-background-300">
                提示：收藏功能为本地演示，数据保存在当前浏览器中，刷新后仍会保留。
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-12 space-y-10 md:space-y-14">
        <section className="bg-background-50 rounded-lg border border-background-200 p-5 md:p-7">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-4">剧情简介</h2>
          <p className="text-sm md:text-base text-foreground-700 leading-loose">{movie.synopsis}</p>
          <div className="mt-5 pt-4 border-t border-background-200 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              更新于 {movie.updatedAt}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-movie-2-line"></i>
              {typeLabel}
            </span>
            <Link
              to={`/category/${movie.type}`}
              className="flex items-center gap-1 text-secondary-600 hover:text-secondary-700 cursor-pointer"
            >
              <i className="ri-links-line"></i>
              进入「{typeLabel}」分类
            </Link>
          </div>
        </section>

        <section>
          <SectionHeading title="影评节选" subtitle="来自本站编辑与评论作者的观点" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {movie.reviews.map((r, i) => (
              <article
                key={i}
                className="bg-background-50 rounded-lg border border-background-200 p-5 md:p-6"
              >
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2.5">
                    <span className="w-9 h-9 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center">
                      <i className="ri-user-3-line"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{r.author}</p>
                      <p className="text-xs text-foreground-500">{r.date}</p>
                    </div>
                  </div>
                  <span className="shrink-0 px-2 py-1 rounded-md bg-accent-100 text-accent-700 text-xs font-semibold">
                    {r.score.toFixed(1)} 分
                  </span>
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed">{r.excerpt}</p>
              </article>
            ))}
          </div>
        </section>

        <section>
          <SectionHeading title="相关推荐" subtitle="根据同类型标签为你推荐" moreTo={`/category/${movie.type}`} />
          {related.length === 0 ? (
            <div className="bg-background-50 rounded-lg border border-background-200">
              <EmptyState icon="ri-emotion-line" title="暂无相关推荐" description="同类作品还在整理中，敬请期待。" />
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {related.map((m) => (
                <MovieCard key={m.id} movie={m} showType={false} />
              ))}
            </div>
          )}
        </section>
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-md bg-foreground-950 text-background-50 text-sm animate-fade-in-fast">
          {toast}
        </div>
      )}
    </div>
  );
}